import { z } from 'zod';
import { idSchema } from './common.js';
import { storeLocationSchema } from './stores.js';
/**
 * Content reports and household overrides (PLAN §8 UGC, §9 #3 and #13).
 * A household reports a thing once; reporter identity never leaves the
 * server (D12) — admins see only how many households reported it.
 */
export const REPORT_TARGET_KINDS = [
    'ingredient_photo',
    'recipe',
    'store_product_price',
    'store_product_location',
];
export const reportTargetKindSchema = z.enum(REPORT_TARGET_KINDS);
export const REPORT_REASONS = ['inaccurate', 'offensive', 'copyright', 'spam', 'other'];
export const reportReasonSchema = z.enum(REPORT_REASONS);
export const REPORT_RESOLUTIONS = ['hidden', 'dismissed', 'removed'];
export const reportResolutionSchema = z.enum(REPORT_RESOLUTIONS);
export const REPORT_NOTE_MAX = 500;
/** Distinct households reporting a photo or public recipe before it is hidden pending review. */
export const CONTENT_REPORT_HIDE_THRESHOLD = 3;
/** Body for POST …/photos/:id/report and POST /recipes/:id/report. */
export const reportRequestSchema = z.object({
    reason: reportReasonSchema,
    note: z.string().trim().max(REPORT_NOTE_MAX).optional(),
});
/** Body for POST /stores/:id/products/:productId/report. */
export const storeProductReportRequestSchema = reportRequestSchema.extend({
    kind: z.enum(['price', 'location']),
});
export const reportResponseSchema = z.object({
    ok: z.literal(true),
    /** True when this report crossed the auto-hide threshold (photos and recipes only). */
    hidden: z.boolean(),
});
// ------------------------------------------------ household price/location --
/** The household's own price and/or location for one product at one store (§9 #3b). */
export const storeProductOverrideSchema = z.object({
    storeId: idSchema,
    productId: idSchema,
    priceCents: z.number().int().nonnegative().nullable(),
    currency: z.string(),
    location: storeLocationSchema.nullable(),
    updatedAt: z.iso.datetime(),
});
/**
 * PUT /stores/:id/products/:productId/override. Omitted fields keep their
 * value; `null` clears one. Clearing both deletes the override.
 */
export const setStoreProductOverrideRequestSchema = z.object({
    priceCents: z.number().int().min(0).max(100000000).nullable().optional(),
    currency: z.string().length(3).optional(),
    location: storeLocationSchema.nullable().optional(),
});
export const storeProductOverrideResponseSchema = z.object({
    override: storeProductOverrideSchema.nullable(),
});
// ------------------------------------------------------------ admin review --
export const adminReportsQuerySchema = z.object({
    status: z.enum(['open', 'resolved']).default('open'),
});
/**
 * One reported target with its open (or one resolved batch of) reports
 * folded together. Never carries a reporter household or user id.
 */
export const adminReportSchema = z.object({
    /** A report id in the batch; resolving it resolves the whole batch. */
    id: idSchema,
    targetKind: reportTargetKindSchema,
    targetId: idSchema,
    storeId: idSchema.nullable(),
    productId: idSchema.nullable(),
    target: z.object({
        /** Recipe title, ingredient name, or "product at store". */
        label: z.string(),
        /** Extra context, e.g. the store or the current shared value. */
        detail: z.string().nullable(),
        /** Image URL for photo reports (admins can see hidden photos). */
        imageUrl: z.string().nullable(),
        hidden: z.boolean(),
        /** False when the target no longer exists. */
        exists: z.boolean(),
    }),
    /** The shared value shown when the report was made (price/location kinds). */
    reportedValue: z.string().nullable(),
    householdCount: z.number().int().nonnegative(),
    reasons: z.array(z.object({ reason: reportReasonSchema, count: z.number().int() })),
    notes: z.array(z.string()),
    firstReportedAt: z.iso.datetime(),
    lastReportedAt: z.iso.datetime(),
    resolvedAt: z.iso.datetime().nullable(),
    resolution: reportResolutionSchema.nullable(),
});
export const adminReportsResponseSchema = z.object({ reports: z.array(adminReportSchema) });
export const adminResolveReportRequestSchema = z.object({ resolution: reportResolutionSchema });
export const adminResolveReportResponseSchema = z.object({
    report: adminReportSchema,
    /** Shared price/location observations hidden by a `hidden`/`removed` resolution. */
    hiddenObservations: z.number().int().nonnegative(),
});
/** Encodes a location as the report's `reportedValue` ("aisle:12"). */
export function locationReportValue(loc) {
    return `${loc.type}:${loc.value.trim().toLowerCase()}`;
}
//# sourceMappingURL=reports.js.map